import React from "react";
import {
  FiTag,
  FiMessageSquare,
  FiPhone,
  FiMail,
  FiBriefcase,
  FiUserCheck,
  FiCheck,
} from "react-icons/fi";

const PRIORITY_STYLES = {
  High: {
    badge: "bg-red-100 text-red-700 border-red-200",
    border: "border-l-red-500",
  },
  Medium: {
    badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
    border: "border-l-yellow-500",
  },
  Low: {
    badge: "bg-green-100 text-green-700 border-green-200",
    border: "border-l-green-500",
  },
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const LeadCard = ({
  lead,
  onOpenDetails,
  draggable = true,
  onDragStart,
  onDragEnd,
  isDragging = false,
  selectMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const priorityKey =
    lead?.priority && PRIORITY_STYLES[lead.priority] ? lead.priority : "Medium";
  const styles = PRIORITY_STYLES[priorityKey];

  // While selection mode is on we disable native drag so the user can
  // freely click cards to (un)check them without accidentally starting a drag.
  const dragEnabled = draggable && !selectMode;

  const handleDragStart = (e) => {
    if (!dragEnabled) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-lead-id", lead._id);
    e.dataTransfer.setData("text/plain", lead._id);
    onDragStart?.(lead);
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    onDragEnd?.();
  };

  const handleCardClick = (e) => {
    if (selectMode) {
      e.stopPropagation();
      onToggleSelect?.(lead._id);
      return;
    }
    onOpenDetails?.(lead);
  };

  const notesCount = Array.isArray(lead?.notes) ? lead.notes.length : 0;
  const salesPersonName =
    lead?.salesPerson && typeof lead.salesPerson === "object"
      ? (
          lead.salesPerson.name ||
          `${lead.salesPerson.firstName || ""} ${
            lead.salesPerson.lastName || ""
          }`
        )
          .replace(/\s+/g, " ")
          .trim() || null
      : null;
  const consultantName =
    lead?.assignedTo && typeof lead.assignedTo === "object"
      ? lead.assignedTo.name ||
        `${lead.assignedTo.firstName || ""} ${lead.assignedTo.lastName || ""}`.trim()
      : null;

  return (
    <div
      draggable={dragEnabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleCardClick}
      className={`
        group bg-white rounded-md border border-l-4 p-3 cursor-pointer
        transition-all duration-150 shadow-sm
        hover:shadow-md hover:-translate-y-0.5
        ${isDragging ? "opacity-40" : "opacity-100"}
        ${styles.border}
        ${
          selectMode && isSelected
            ? "border-blue-500 ring-2 ring-blue-300 bg-blue-50/40"
            : "border-gray-200"
        }
      `}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectMode && (
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                isSelected
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-gray-300"
              }`}
              aria-label={isSelected ? "Selected" : "Not selected"}
            >
              {isSelected && <FiCheck size={12} strokeWidth={3} />}
            </div>
          )}
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
            {getInitials(lead?.name)}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-800 text-sm truncate">
              {lead?.name || "Unnamed lead"}
            </h4>
            {lead?.email && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <FiMail size={11} />
                <span className="truncate">{lead.email}</span>
              </p>
            )}
          </div>
        </div>

        <span
          className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border flex-shrink-0 ${styles.badge}`}
        >
          {priorityKey}
        </span>
      </div>

      <div className="space-y-1 text-xs text-gray-600">
        {lead?.phone && (
          <div className="flex items-center gap-1.5 truncate">
            <FiPhone size={12} className="flex-shrink-0" />
            <span className="truncate">{lead.phone}</span>
          </div>
        )}
        {salesPersonName && (
          <div className="flex items-center gap-1.5 truncate text-indigo-700">
            <FiUserCheck size={12} className="flex-shrink-0" />
            <span className="truncate">SP: {salesPersonName}</span>
          </div>
        )}
        {consultantName && (
          <div className="flex items-center gap-1.5 truncate text-blue-700">
            <FiBriefcase size={12} className="flex-shrink-0" />
            <span className="truncate">Consultant: {consultantName}</span>
          </div>
        )}
        {lead?.category && (
          <div>
            <span className="inline-block bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[11px]">
              {lead.category}
            </span>
          </div>
        )}
        {lead?.expectedDealRevenue && (
          <div className="text-green-700 font-semibold">
            PKR {""} {Number(lead.expectedDealRevenue).toLocaleString()}
          </div>
        )}
      </div>

      {Array.isArray(lead?.tags) && lead.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {lead.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px]"
            >
              <FiTag size={10} />
              {tag}
            </span>
          ))}
          {lead.tags.length > 3 && (
            <span className="inline-flex items-center bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px]">
              +{lead.tags.length - 3}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500">
        <span className="flex items-center gap-1">
          <FiMessageSquare size={12} />
          {notesCount} {notesCount === 1 ? "note" : "notes"}
        </span>
        {lead?.leadSource && (
          <span className="truncate ml-2 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px]">
            {lead.leadSource === "Other"
              ? lead.leadSourceOther
              : lead.leadSource}
          </span>
        )}
      </div>
    </div>
  );
};

export default LeadCard;
